import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Plane, ArrowLeft, Eye } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

import StudentMobileNav from "@/components/student/StudentMobileNav";
import NAV_TABS from "@/components/student/navConfig";
import DashboardTab from "@/components/dashboard/DashboardTab";
import DecisionsTab from "@/components/decisions/DecisionsTab";
import CompanyTab from "@/components/dashboard/CompanyTab";
import MarketTab from "@/components/dashboard/MarketTab";
import RouteMapSection from "@/components/dashboard/RouteMapSection";
import PerformanceHistory from "@/components/dashboard/PerformanceHistory";
import TeamCollaboration from "@/components/dashboard/TeamCollaboration";
import AIAssist from "@/components/dashboard/AIAssist";
import Achievements from "@/components/dashboard/Achievements";

export default function InstructorTeamView() {
  const { gameId, teamId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [game, setGame] = useState(null);
  const [team, setTeam] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [decision, setDecision] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");

  useEffect(() => {
    fetchData();
  }, [gameId, teamId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [gameRes, teamRes] = await Promise.all([
        axios.get(`${API}/games/${gameId}`),
        axios.get(`${API}/teams/${teamId}`)
      ]);
      setGame(gameRes.data);
      setTeam(teamRes.data);

      if (teamRes.data && gameRes.data.current_quarter > 0) {
        const [kpisRes, decisionRes] = await Promise.all([
          axios.get(`${API}/teams/${teamId}/kpis`),
          axios.get(`${API}/teams/${teamId}/decisions/${gameRes.data.current_quarter}`)
        ]);
        setKpis(kpisRes.data);
        setDecision(decisionRes.data);
      }
    } catch (error) {
      toast.error("Failed to load team data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-slate-200 border-t-sky-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading team view...</p>
        </div>
      </div>
    );
  }

  const noopUpdate = () => {};
  const noopRefresh = () => fetchData();

  const TAB_CONTENT = {
    dashboard: <DashboardTab game={game} team={team} kpis={kpis} onRefresh={noopRefresh} />,
    decisions: <DecisionsTab game={game} team={team} decision={decision} setDecision={setDecision} onRefresh={noopRefresh} readOnly />,
    company: <CompanyTab game={game} team={team} />,
    market: <MarketTab game={game} team={team} />,
    routemap: <RouteMapSection game={game} team={team} />,
    history: <PerformanceHistory game={game} team={team} />,
    team: <TeamCollaboration game={game} team={team} readOnly />,
    ai: <AIAssist game={game} team={team} />,
    achievements: <Achievements team={team} />,
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="topbar">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left */}
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="text-slate-400 hover:text-white hover:bg-slate-800"
                data-testid="close-team-view-btn"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-3">
                <img src="/skyboard-logo.png" alt="SkyBoard" className="h-10 w-auto" />
                <div>
                  <h1 className="text-lg font-heading font-bold text-white tracking-wide">
                    {team?.name || "SKYBOARD"}
                  </h1>
                  <p className="text-xs text-slate-400">
                    {team?.airline_code} &bull; Q{game?.current_quarter} of {game?.quarters_count}
                  </p>
                </div>
              </div>
            </div>

            {/* Center nav */}
            <nav className="hidden md:flex items-center gap-1">
              {NAV_TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                    activeTab === tab.id
                      ? "text-white border-sky-500"
                      : "text-slate-400 border-transparent hover:text-white"
                  }`}
                  data-testid={`nav-tab-${tab.id}`}
                >
                  <tab.icon className="w-4 h-4" />
                  {t(tab.labelKey)}
                </button>
              ))}
            </nav>

            {/* Right */}
            <div className="flex items-center gap-2">
              <LanguageSwitcher variant="header" />
              <div className="text-right hidden sm:block">
                <p className="text-sm text-white font-medium">{user?.name}</p>
                <p className="text-xs text-slate-400">{game?.name}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Read-only banner */}
      <div className="bg-amber-50 border-b border-amber-200" data-testid="readonly-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center gap-2">
          <Eye className="w-4 h-4 text-amber-600" />
          <span className="text-sm text-amber-700 font-medium">
            Viewing as instructor &mdash; read-only mode
          </span>
        </div>
      </div>

      <StudentMobileNav activeTab={activeTab} setActiveTab={setActiveTab} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" key={activeTab}>
        {TAB_CONTENT[activeTab]}
      </main>
    </div>
  );
}
